import { Box, Button, Stack, Typography, IconButton, Tooltip, Divider } from '@mui/material'
import {
  Add as AddIcon,
  SupportAgent as CaseIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  MergeType as MergeIcon,
  Label as LabelIcon,
} from '@mui/icons-material'
import { DataTable } from '../../components/ui/DataTable'
import { SearchBar } from '../../components/ui/SearchBar'
import { StatusBadge } from '../../components/ui/StatusBadge'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { themeColors } from '../../app/layout/theme'
import { Case } from '../../types'
import { useState } from 'react'

interface CaseListProps {
  cases: Case[]
  isLoading: boolean
  onNewClick: () => void
  onSearch: (query: string) => void
  onCaseClick?: (caseRecord: Case) => void
}

export const CaseList = ({ cases, isLoading, onNewClick, onSearch, onCaseClick }: CaseListProps) => {
  const [searchQuery, setSearchQuery] = useState('')

  const columns = [
    { id: 'caseNumber', label: 'Case Number' },
    { id: 'subject', label: 'Subject' },
    {
      id: 'status',
      label: 'Status',
      render: (val: string) => <StatusBadge status={val} />,
    },
    {
      id: 'priority',
      label: 'Priority',
      render: (val: string) => <StatusBadge status={val} />,
    },
    { id: 'owner', label: 'Case Owner Alias' },
  ]

  if (isLoading) return <LoadingSpinner fullHeight label="Loading cases..." />

  return (
    <Box>
      {/* Toolbar */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: themeColors.surface,
          borderBottom: `1px solid ${themeColors.border}`,
        }}
      >
        <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary }}>
          {cases.length} item{cases.length !== 1 ? 's' : ''} · Updated a few seconds ago
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
            onClick={onNewClick}
            sx={{
              fontSize: '13px',
              background: themeColors.primary, '&:hover': { background: themeColors.primaryDark },
              boxShadow: '0 2px 6px rgba(0,112,210,0.3)',
            }}
          >
            New
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<MergeIcon sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: '13px',
              borderColor: themeColors.border,
              color: themeColors.text.primary,
              '&:hover': { borderColor: themeColors.primary, color: themeColors.primary, backgroundColor: themeColors.primaryLight },
            }}
          >
            Merge Cases
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LabelIcon sx={{ fontSize: 15 }} />}
            sx={{
              fontSize: '13px',
              borderColor: themeColors.border,
              color: themeColors.text.primary,
              '&:hover': { borderColor: themeColors.primary, color: themeColors.primary, backgroundColor: themeColors.primaryLight },
            }}
          >
            Assign Label
          </Button>
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, borderColor: themeColors.border }} />
          <Tooltip title="Refresh">
            <IconButton size="small" sx={{ color: themeColors.text.secondary }}>
              <RefreshIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filters">
            <IconButton size="small" sx={{ color: themeColors.text.secondary }}>
              <FilterIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Empty state */}
      {cases.length === 0 ? (
        <Box
          sx={{
            py: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              backgroundColor: `${themeColors.error}12`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CaseIcon sx={{ fontSize: 40, color: themeColors.error, opacity: 0.6 }} />
          </Box>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: themeColors.text.primary }}>
            Track customer support in one place
          </Typography>
          <Typography sx={{ fontSize: '13px', color: themeColors.text.secondary, maxWidth: 360, textAlign: 'center' }}>
            Cases bring together customer questions, feedback, and issues from any channel.
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={onNewClick}
            sx={{
              mt: 1,
              background: themeColors.primary, '&:hover': { background: themeColors.primaryDark },
              boxShadow: '0 2px 6px rgba(0,112,210,0.3)',
              px: 3,
            }}
          >
            Add a Case
          </Button>
        </Box>
      ) : (
        <Box sx={{ p: 3 }}>
          <Stack spacing={2}>
            <SearchBar
              value={searchQuery}
              onSearch={(v) => { setSearchQuery(v); onSearch(v) }}
              placeholder="Search cases..."
            />
            <DataTable
              columns={columns}
              rows={cases}
              onRowClick={onCaseClick}
              emptyMessage="No cases found"
              selectable
              showRowNumbers
            />
          </Stack>
        </Box>
      )}
    </Box>
  )
}
